import React, { useEffect, useRef, useState, type FormEvent } from 'react';
import { Camera, ImageIcon, Loader2, Trash2 } from 'lucide-react';
import type { ProfileDTO, ProfileUpdate } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Combobox } from '../../components/ui/Combobox';
import { Dialog } from '../../components/ui/Dialog';
import { Input, Label } from '../../components/ui/Input';
import { useLanguage } from '../../lib/i18n';
import { IS_NATIVE } from '../../lib/platform';
import { AVATAR_ACCEPT, prepareAvatar } from './avatarImage';
import { CameraCaptureDialog } from './CameraCaptureDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { departmentOptions, roleOptions } from './profileOptions';

interface ProfileDialogProps {
  open: boolean;
  profile: ProfileDTO;
  onClose: () => void;
  onSaved: (profile: ProfileDTO) => void;
  onNotify: (kind: ToastKind, text: string) => void;
}

interface ProfileForm {
  displayName: string;
  pronouns: string;
  department: string;
  jobTitle: string;
}

const TEXT_LIMIT = 80;

/** Ayarlar > Genel > "Düzenle" penceresi. */
export const ProfileDialog: React.FC<ProfileDialogProps> = ({ open, profile, onClose, onSaved, onNotify }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState<ProfileForm>({ displayName: '', pronouns: '', department: '', jobTitle: '' });
  /** undefined: fotoğraf değişmedi · null: kaldırılacak · dize: yeni fotoğraf. */
  const [avatarDraft, setAvatarDraft] = useState<string | null | undefined>(undefined);
  const [nameError, setNameError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [isPreparingAvatar, setIsPreparingAvatar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);

  // Form yalnızca pencere AÇILIRKEN kayıtlı profilden dolduruluyor. Profil
  // nesnesi pencere açıkken de değişebiliyor (şifre değiştirildiğinde);
  // o anda formu sıfırlamak kullanıcının yazdıklarını silerdi.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setForm({
        displayName: profile.displayName,
        pronouns: profile.pronouns,
        department: profile.department,
        jobTitle: profile.jobTitle,
      });
      setAvatarDraft(undefined);
      setNameError(null);
      setAvatarError(null);
    }
    wasOpenRef.current = open;
  }, [open, profile]);

  const customAvatar = avatarDraft === undefined ? profile.avatarUrl : avatarDraft;
  const previewSrc = customAvatar ?? profile.googleAvatarDataUrl ?? null;
  const isDirty =
    avatarDraft !== undefined ||
    form.displayName !== profile.displayName ||
    form.pronouns !== profile.pronouns ||
    form.department !== profile.department ||
    form.jobTitle !== profile.jobTitle;

  const update = (field: keyof ProfileForm) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'displayName') setNameError(null);
  };

  const pickPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Aynı dosya yeniden seçilebilsin.
    event.target.value = '';
    if (!file) return;
    setAvatarError(null);
    setIsPreparingAvatar(true);
    try {
      setAvatarDraft(await prepareAvatar(file));
    } catch (err) {
      setAvatarError((err as Error).message);
    } finally {
      setIsPreparingAvatar(false);
    }
  };

  const close = () => {
    if (isDirty && !window.confirm(t('Kaydedilmemiş değişiklikler var. Kapatılsın mı?', 'You have unsaved changes. Close anyway?'))) {
      return;
    }
    onClose();
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const displayName = form.displayName.trim();
    if (!displayName) {
      setNameError(t('Ad boş bırakılamaz.', 'Name cannot be empty.'));
      return;
    }

    const patch: ProfileUpdate = {
      displayName,
      pronouns: form.pronouns,
      department: form.department,
      jobTitle: form.jobTitle,
    };
    if (avatarDraft !== undefined) patch.avatarUrl = avatarDraft;

    setIsSaving(true);
    try {
      onSaved(await window.zabit.updateProfile(patch));
      onNotify('success', t('Profil kaydedildi.', 'Profile saved.'));
      onClose();
    } catch (err) {
      onNotify('error', (err as Error).message || t('Profil kaydedilemedi.', 'Could not save your profile.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        title={t('Profilin', 'Your profile')}
        footer={
          <Button type="submit" form="profile-form" disabled={isSaving || isPreparingAvatar}>
            {isSaving && <Loader2 className="size-4 animate-spin" />}
            {t('Bitir', 'Finish')}
          </Button>
        }
      >
        <form id="profile-form" onSubmit={(event) => void save(event)} className="space-y-5" noValidate>
          {/* Fotoğraf */}
          <div className="space-y-2">
            <Label>{t('Fotoğrafın', 'Your photo')}</Label>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Avatar src={previewSrc} name={form.displayName || profile.email} className="size-14 text-base" />
                {customAvatar && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={isSaving}
                    // Kayıtlı özel fotoğraf yoksa kaldırmak yalnızca taslağı geri alır.
                    onClick={() => setAvatarDraft(profile.avatarUrl ? null : undefined)}
                  >
                    <Trash2 className="size-4" />
                    {t('Kaldır', 'Remove')}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={isPreparingAvatar || isSaving}
                  title={IS_NATIVE ? t('Galeriden seç', 'Choose from gallery') : t('Bilgisayardan seç', 'Choose from your computer')}
                  aria-label={IS_NATIVE ? t('Galeriden seç', 'Choose from gallery') : t('Bilgisayardan seç', 'Choose from your computer')}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isPreparingAvatar ? <Loader2 className="size-4 animate-spin" /> : <ImageIcon className="size-4" />}
                </Button>
                {/* Telefonda yok: uygulama kamera izni istemiyor (manifestte CAMERA yok,
                    Play'de ayrıca beyan gerektirirdi); fotoğraf galeriden seçiliyor. */}
                {!IS_NATIVE && (
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={isSaving}
                    title={t('Kameradan çek', 'Take a photo')}
                    aria-label={t('Kameradan çek', 'Take a photo')}
                    onClick={() => setIsCameraOpen(true)}
                  >
                    <Camera className="size-4" />
                  </Button>
                )}
              </div>
            </div>
            {avatarError && (
              <p role="alert" className="text-[0.8rem] font-medium text-destructive">
                {avatarError}
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={AVATAR_ACCEPT}
              className="hidden"
              onChange={(event) => void pickPhoto(event)}
            />
          </div>

          {/* Ad soyad */}
          <div className="grid gap-2">
            <Label htmlFor="profile-name">{t('Ad soyad', 'Full name')}</Label>
            <Input
              id="profile-name"
              value={form.displayName}
              onChange={(event) => update('displayName')(event.target.value)}
              maxLength={TEXT_LIMIT}
              autoComplete="name"
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? 'profile-name-error' : undefined}
            />
            {nameError && (
              <p id="profile-name-error" role="alert" className="text-[0.8rem] font-medium text-destructive">
                {nameError}
              </p>
            )}
          </div>

          {/* Zamirler */}
          <div className="grid gap-2">
            <Label htmlFor="profile-pronouns">{t('Zamirler', 'Pronouns')}</Label>
            <Input
              id="profile-pronouns"
              value={form.pronouns}
              onChange={(event) => update('pronouns')(event.target.value)}
              maxLength={TEXT_LIMIT}
              placeholder={t('ör. o/ona, she/her, they/them', 'e.g. she/her, he/him, they/them')}
            />
          </div>

          {/* E-posta — Google hesabına bağlı, değiştirilemez */}
          <div className="grid gap-2">
            <Label htmlFor="profile-email">{t('E-posta', 'Email')}</Label>
            <Input id="profile-email" value={profile.email} disabled readOnly aria-describedby="profile-email-hint" />
            <p id="profile-email-hint" className="text-[0.8rem] text-muted-foreground">
              {t(
                'Google ile giriş yaptığın için e-posta adresi buradan değiştirilemez.',
                'You sign in with Google, so your email address can’t be changed here.'
              )}
            </p>
          </div>

          {/* Şifre — tıklayınca şifre penceresi açılır */}
          <div className="grid gap-2">
            <Label htmlFor="profile-password">{t('Şifre', 'Password')}</Label>
            <button
              id="profile-password"
              type="button"
              onClick={() => setIsPasswordOpen(true)}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-field px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] hover:border-ring focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30"
            >
              <span className={profile.hasPassword ? 'tracking-[0.2em] text-foreground' : 'text-muted-foreground'}>
                {profile.hasPassword ? '••••••••••' : t('Şifre belirle', 'Set a password')}
              </span>
              <span className="text-[0.8rem] font-medium text-brand">
                {profile.hasPassword ? t('Değiştir', 'Change') : t('Belirle', 'Set')}
              </span>
            </button>
          </div>

          {/* Departman */}
          <div className="grid gap-2">
            <Label id="profile-department-label" htmlFor="profile-department">
              {t('Departman', 'Department')}
            </Label>
            <Combobox
              id="profile-department"
              aria-labelledby="profile-department-label"
              value={form.department}
              options={departmentOptions()}
              onChange={update('department')}
              placeholder={t('Departmanını seç', 'Select your department')}
              searchPlaceholder={t('Ara ya da yaz…', 'Search or type…')}
            />
          </div>

          {/* Rol */}
          <div className="grid gap-2">
            <Label id="profile-role-label" htmlFor="profile-role">
              {t('Rol', 'Role')}
            </Label>
            <Combobox
              id="profile-role"
              aria-labelledby="profile-role-label"
              value={form.jobTitle}
              options={roleOptions()}
              onChange={update('jobTitle')}
              placeholder={t('Rolünü seç', 'Select your role')}
              searchPlaceholder={t('Ara ya da yaz…', 'Search or type…')}
            />
          </div>
        </form>
      </Dialog>

      <CameraCaptureDialog
        open={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={(dataUrl) => {
          setAvatarError(null);
          setAvatarDraft(dataUrl);
        }}
      />

      <ChangePasswordDialog
        open={isPasswordOpen}
        hasPassword={profile.hasPassword}
        onClose={() => setIsPasswordOpen(false)}
        onChanged={onSaved}
        onNotify={onNotify}
      />
    </>
  );
};
